import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Center, CenterProps, useColorModeValue, Text, Icon, IconButton } from '@chakra-ui/react'
import { Property, UsersPermissionsUser, useUpdateMyUserMutation } from 'generated/graphql'
import { useAuthContext } from 'context/AuthProvider'

import { VscClose, VscHeart } from 'react-icons/vsc'
import { FaHeart } from 'react-icons/fa'
import { useHistory } from 'react-router'
import CustomTooltip from 'components/CustomTooltip'

type FavoritePropertyProps = {
  propertyData: Property | null
  remove?: boolean
  withLabel?: boolean
} & CenterProps

const FavoriteProperty: React.FC<FavoritePropertyProps> = ({
  propertyData: property,
  remove,
  withLabel,
  ...rest
}) => {
  const { setUser, user, isAuthenticated } = useAuthContext()
  const [updateMyUser] = useUpdateMyUserMutation({
    onCompleted: ({ updateMyUser }) => {
      if (setUser) {
        setUser(
          updateMyUser ? (updateMyUser as UsersPermissionsUser) : (user as UsersPermissionsUser)
        )
      }
    }
  })
  const history = useHistory()
  const color = useColorModeValue('gray.800', 'white')
  const { t } = useTranslation()
  const isFavorite = user?.favorites_properties?.find(
    (p) => p?.id.toString() === property?.id.toString()
  )

  return (
    <CustomTooltip
      label={
        isFavorite
          ? t('search_results.remove_from_favourite')
          : t('search_results.add_to_favourite')
      }
    >
      <Center
        onClick={async () => {
          try {
            if (isAuthenticated) {
              await updateMyUser({
                variables: {
                  input: {
                    favorites_properties: (isFavorite
                      ? (user?.favorites_properties
                          ?.filter((p) => p?.id.toString() !== property?.id)
                          .map((p) => p?.id?.toString()) as string[])
                      : [
                          ...(user?.favorites_properties
                            ? (user?.favorites_properties?.map((p) =>
                                p?.id?.toString()
                              ) as string[])
                            : []),
                          property?.id
                        ]) as string[]
                  }
                }
              })
            } else history.push('/login')
          } catch (error) {
            console.log(error)
          }
        }}
        color={isFavorite ? 'red.300' : color}
        {...rest}
      >
        {withLabel && (
          <Text fontSize="x-small" fontWeight="semibold" pointerEvents="none" mr={2}>
            {remove
              ? t('search_results.remove')
              : isFavorite
              ? t('search_results.saved')
              : t('search_results.save')}
          </Text>
        )}

        <IconButton
          isRound
          bg={useColorModeValue('white', 'gray.600')}
          aria-label="favorites"
          size="sm"
          _hover={{ transform: 'scale(1.1)' }}
          sx={{ ':hover > svg': { transform: 'scale(1.1)' } }}
          transition="all 0.15s ease"
          icon={
            <Icon
              color={isFavorite ? 'red.300' : color}
              as={remove ? VscClose : isFavorite ? FaHeart : VscHeart}
              transition="all 0.15s ease"
            />
          }
          border="1px solid"
          borderColor={useColorModeValue('gray.100', 'gray.600')}
        />
      </Center>
    </CustomTooltip>
  )
}
FavoriteProperty.defaultProps = {
  withLabel: false
}

export default memo(FavoriteProperty)
